import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { Select } from 'antd';
import type { LabeledValue, RenderSettingsScope } from 'common/models';
import { getScopeTranslateKey } from 'helpers/renderSettings';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';

import RenderSettingsItem from './RenderSettingsItem';

interface Props {
  options: string[];
  selectedFieldsConfig: string[];
  onFieldIdChange: (oldFieldId: string, newFieldId: string) => void;
  onRemoveClick: (fieldId: string) => void;
  scope: RenderSettingsScope;
  enableRemove: boolean;
  handleSortEnd: (oldIndex: number, newIndex: number) => void;
  showAdd: boolean;
  handleAddField: (newFieldId: string) => void;
  // Use for dynamic options, like serivceObjectRevisionCategories
  allAdditionalOptions?: LabeledValue[];
  filteredAdditionalOptions?: LabeledValue[];
}

const RenderSettingsList: React.FC<Props> = (props) => {
  const {
    options,
    selectedFieldsConfig,
    onFieldIdChange,
    onRemoveClick,
    scope,
    enableRemove,
    handleSortEnd,
    handleAddField,
    showAdd,
    allAdditionalOptions,
    filteredAdditionalOptions
  } = props;

  const { t } = useCustomTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedFieldsConfig.findIndex((x) => x === active.id);
      const newIndex = selectedFieldsConfig.findIndex((x) => x === over.id);

      handleSortEnd(oldIndex, newIndex);
    }
  };

  return (
    <div className="RenderSettingsList">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext items={selectedFieldsConfig} strategy={verticalListSortingStrategy}>
          {selectedFieldsConfig.map((itemId) => (
            <RenderSettingsItem
              key={itemId}
              value={itemId}
              options={options}
              onFieldIdChange={onFieldIdChange}
              onRemoveClick={onRemoveClick}
              scope={scope}
              enableRemove={enableRemove}
              allAdditionalOptions={allAdditionalOptions}
              filteredAdditionalOptions={filteredAdditionalOptions}
            />
          ))}
        </SortableContext>
        {showAdd && (
          <div className="RenderSettingsItemToAadd">
            <Select
              placeholder={t('renderSettings.addColumnButton')}
              onChange={(newValue) => handleAddField(newValue as any as string)}
              className="RenderSettingsItem_select"
              // After selecting column it keep visible placeholder
              value={null}
              options={[
                ...options.map((option) => ({
                  value: option,
                  label: t(`renderSettings.labels.${getScopeTranslateKey(scope)}.${option}`),
                  title: t(`renderSettings.labels.${getScopeTranslateKey(scope)}.${option}`)
                })),
                ...(filteredAdditionalOptions || [])
              ]}
            />
          </div>
        )}
      </DndContext>
    </div>
  );
};

export default RenderSettingsList;
